import React, { Fragment, useEffect, useState } from 'react';
import { Table as BsTable } from 'react-bootstrap';
import Button from 'tradevan-ui/Button';
import Checkbox from 'tradevan-ui/Checkbox';
import Pagination from 'components/Table/components/Pagination';
import { inputVal, isNull, isUndefined } from '../../tradevan-ui/consts';
import Input from '../../tradevan-ui/Input';
import Select from '../../tradevan-ui/Select';
import { Iobject } from '../../tradevan-ui/type';
import { ItableColumn, TableProps } from './type';
import TbLoadingMask from './components/LoadingMask';
import { FIRST_PAGE, timerDebounce } from './consts';

/**
 * 1. 【column】
 *      一. field = itemNo，可直接於 data 插入項次(index)。
 *      二. selected 開啟(default: true)且資料內需有 [selectedField] 的欄位(default: 'selected')，才有選取 row 的功能。
 * 2. seletected 要寫回資料，必須有 onDataChange 的屬性
 *    ex: onDataChange={handleDataChange}
          const handleDataChange: TableProps<Idetail>['onDataChange'] = (
            rows,
            rowStart,
            rowEnd,
          ) => {
            setDetails((prev) =>
              prev.map((obj, idx) =>
                idx >= rowStart && idx <= rowEnd ? rows[idx - rowStart] : obj,
              ),
            );
          };
 * 3. page, pageSize 可由外部控制, 內部用 useEffect 同步
 * 4. serverRender 模式, 記得傳入 page, pageSize, count, onPageChange 並設定 serverRender={true}
 *
 * @param props TableProps
 * @returns
 */
const Table = <T extends { id: string | number }>(props: TableProps<T>) => {
  const {
    style,
    serverRender = false,
    columns,
    data,
    variant,
    theadVariant: thVariant = '',
    borderStyle,
    hover = true,
    size,
    page: pageTemp = FIRST_PAGE,
    /** 預設顯示10筆 */
    pageSize: pageSizeTemp = 10,
    /** 預設第一個選項為 pageSize的值(pageSize預設10) */
    pageSizeOptions = [pageSizeTemp, 10, 25, 50, 100],
    /** 預設是資料長度 */
    totalCount = data.length,
    paginationProps,
    tableClass = '',
    theadClass: thClassName = '',
    thStyle = {},
    selectedField = 'selected',
    editField = 'edit',
    itemNoField = 'itemNo',
    onPageChange,
    onDataChange,
    renderCell,
    sort,
    showLoadingMask = true,
  } = props;

  const [rows, setRows] = useState<T[]>([]);
  const [page, setPage] = useState(pageTemp);
  const [showLoading, setShowLoading] = useState(false);

  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);
  /** 要顯示的筆數起始； serverRender 永遠是第一頁，所以是0 */
  const rowStart = serverRender ? 0 : page * pageSize;
  /** 要顯示的筆數結束； serverRender 永遠是得到資料的長度 .length */
  const getRowEnd = (): number => {
    const temp = (page + 1) * pageSize - 1;
    if (serverRender) {
      return data.length;
    }
    // 如果計算出來的長度高於資料長度，那就顯示資料長度
    return temp > data.length ? data.length : temp;
  };

  const rowEnd = getRowEnd();

  const dataChangeTrigger = (newData: T[]) => {
    if (onDataChange) {
      onDataChange(newData, rowStart, rowEnd);
    }
  };

  const createEditInput = (
    row: T,
    column: ItableColumn,
    rowIndex: number,
    isEdit: boolean,
  ) => {
    const {
      field,
      editorOption = [],
      editor = 'text',
      tdStyle,
      width,
    } = column;
    const val = inputVal((row as unknown as Iobject)[field]);
    const returnInput = () => {
      switch (editor) {
        case 'text':
          return (
            <Input
              style={{ width, ...tdStyle }}
              type="text"
              name={field}
              value={val}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => {
                const { value } = e.target;
                setRows((prev) => {
                  const newData = prev.map((obj, idx) => ({
                    ...obj,
                    [field]: idx === rowIndex ? value : (obj as Iobject)[field],
                  }));
                  dataChangeTrigger(newData);
                  return newData;
                });
              }}
            />
          );
        case 'number':
          return (
            <Input
              style={{ width, ...tdStyle }}
              type="number"
              name={field}
              value={val}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => {
                e.stopPropagation();
                const { value } = e.target;
                setRows((prev) => {
                  const newData = prev.map((obj, idx) => ({
                    ...obj,
                    [field]: idx === rowIndex ? value : (obj as Iobject)[field],
                  }));
                  dataChangeTrigger(newData);
                  return newData;
                });
              }}
            />
          );
        case 'select':
          return (
            <Select
              style={{ width: '100%', ...tdStyle }}
              options={editorOption}
              name={field}
              value={val}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => {
                e.stopPropagation();
                const { value } = e.target;
                setRows((prev) => {
                  const newData = prev.map((obj, idx) => ({
                    ...obj,
                    [field]: idx === rowIndex ? value : (obj as Iobject)[field],
                  }));
                  dataChangeTrigger(newData);
                  return newData;
                });
              }}
            />
          );
        default:
          return undefined;
      }
    };
    if (isEdit) {
      return returnInput();
    }
    if (isUndefined(val) || isNull(val)) {
      return val;
    }
    return String(val);
  };
  /** 產生 row 資料 */
  const createRowTd = (row: T, column: ItableColumn, rowIndex: number) => {
    const { field } = column;
    const selectedVal = (row as Iobject)[selectedField];
    const isEditVal = (row as Iobject)[editField];
    const checkBoxHidden = Boolean((row as Iobject).selectHidden);
    switch (field) {
      case selectedField:
        if (isUndefined(selectedVal) || checkBoxHidden) {
          return undefined;
        }
        return (
          // #region ROW的checkBox
          <Checkbox
            // 不加onclock log會報錯誤
            onChange={() => {}}
            checked={!!selectedVal}
            name={`check${rowIndex}`}
            label=""
            // checked={!!selectedVal}
          />
          // #endregion
        );

      case itemNoField:
        return String((row as unknown as Iobject)[column.field]);

      case editField:
        if (isUndefined(isEditVal)) {
          return undefined;
        }
        return (
          <Button
            style={{ minWidth: 'max-content' }}
            onClick={(e) => {
              e.stopPropagation();
              setRows((prev) => {
                const newData = prev.map((obj, idx) => {
                  return {
                    ...obj,
                    [editField]:
                      idx === rowIndex
                        ? !isEditVal
                        : (obj as Iobject)[editField],
                  };
                });
                dataChangeTrigger(newData);
                return newData;
              });
            }}
          >
            {isEditVal ? '儲存' : '編輯'}
          </Button>
        );
      default:
        return renderCell
          ? renderCell({
              field,
              value: (row as Iobject)[field] as string,
              row,
              column,
              rows,
              rowIndex: rowIndex + rowStart, // dataIndex
            }) || createEditInput(row, column, rowIndex, !!isEditVal)
          : createEditInput(row, column, rowIndex, !!isEditVal);
    }
  };

  useEffect(() => {
    setRows(
      (data || [])
        .filter((obj, idx) => idx >= rowStart && idx <= rowEnd)
        .map((obj, idx) => ({
          ...obj,
          // 插入項次(itemNo)
          itemNo: (serverRender ? page * pageSize : 0) + rowStart + idx + 1,
        })),
    );
  }, [data, pageSize, page]);

  // #region page, pageSize 監聽

  /** 外部 page 改變時，同步更新 page */
  useEffect(() => {
    setPage(pageTemp - FIRST_PAGE === 0 ? 0 : pageTemp - 1);
  }, [pageTemp]);

  /** 外部 pageSize 改變時，同步更新 pageSize */
  useEffect(() => {
    setPageSize(pageSizeTemp);
  }, [pageSizeTemp]);
  // #endregion

  return (
    <>
      {/* 只在serverRender = true 才會有 LoadingMask */}
      <BsTable
        style={{ ...style, position: 'relative' }}
        className={tableClass ? `${tableClass} ` : ''}
        bordered={borderStyle === 'bordered'}
        borderless={borderStyle === 'borderless'}
        variant={variant}
        hover={hover}
        size={size}
      >
        <thead
          className={
            (thClassName ? `${thClassName} ` : '') +
            (thVariant ? `thead-${thVariant} ` : '')
          }
          style={{
            textAlign: 'center',
            ...thStyle,
          }}
        >
          <tr>
            {columns
              .filter((v) => {
                const { show = true } = v;
                return show;
              })
              .map((column, columnIdx) => {
                const { field, title } = column;
                const currentRows = rows;
                const someTrue = currentRows.some(
                  (obj) => (obj as Iobject)[selectedField] === true,
                );
                const somefalse = currentRows.some(
                  (obj) => (obj as Iobject)[selectedField] === false,
                );
                return (
                  <Fragment key={`${title}${field}`}>
                    <th
                      style={{
                        ...column.thStyle,
                      }}
                    >
                      {column.field === selectedField ? (
                        <div className="d-md-block">
                          <Checkbox
                            name={`thCheck${columnIdx}`}
                            label={column.title}
                            checked={someTrue && !somefalse}
                            onChange={(e) => {
                              const { checked } = e.target;
                              // 如果是 undefined 畫面不會顯示check
                              const dataValue = (obj: T) =>
                                isUndefined((obj as Iobject)[selectedField])
                                  ? undefined
                                  : checked;

                              setRows((prev) => {
                                const newData = prev.map((obj) => ({
                                  ...obj,
                                  [selectedField]: dataValue(obj),
                                }));
                                dataChangeTrigger(newData);
                                return newData;
                              });
                            }}
                          />
                        </div>
                      ) : (
                        column.title
                      )}
                    </th>
                  </Fragment>
                );
              })}
          </tr>
        </thead>
        <tbody style={{ backgroundColor: 'white' }}>
          {rows.length > 0 ? (
            rows.map((row, rowIndex) => {
              const haveSelected = columns.filter((v) => {
                const { show = true, field } = v;
                return show && field === selectedField;
              });
              const selectedVal = !!(row as Iobject).selected;
              const idVal = String((row as Iobject).id);
              // const checkBoxVisible = Boolean((row as Iobject).selectIsVisible);
              return (
                <tr
                  key={idVal}
                  // 有設定 backgroundColor會影響 hover的style
                  style={
                    selectedVal && haveSelected.length > 0
                      ? { backgroundColor: '#fff6eb' }
                      : {}
                  }
                  // #region 整列的資料 欄位選取事件
                  onClick={() => {
                    if (!isUndefined(selectedVal) && haveSelected.length > 0) {
                      setRows((prev) => {
                        const newData = prev.map((obj, idx) => {
                          const dataValue = (row as Iobject)[selectedField];
                          return {
                            ...obj,
                            [selectedField]:
                              idx === rowIndex && !isUndefined(dataValue) // 不是undefined的才會勾選
                                ? !dataValue
                                : (obj as Iobject)[selectedField],
                          };
                        });
                        dataChangeTrigger(newData);
                        return newData;
                      });
                    }
                  }}
                  // #endregion
                >
                  {columns
                    .filter((v) => {
                      const { show = true } = v;
                      return show;
                    })
                    .map((column) => {
                      const { field, tdStyle, title } = column;
                      return (
                        <td
                          data-title={
                            typeof column.title === 'string'
                              ? column.title
                              : column.dataTitle
                          }
                          key={`${field}${title}${row.id}`}
                          style={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            width: 0,
                            ...tdStyle,
                          }}
                          align={
                            field === selectedField || field === 'itemNo'
                              ? 'center'
                              : 'left'
                          }
                        >
                          {createRowTd(row, column, rowIndex)}
                        </td>
                      );
                    })}
                </tr>
              );
            })
          ) : (
            // 無資料
            <tr>
              <td colSpan={columns.length} style={{ textAlign: 'center' }}>
                無資料
              </td>
            </tr>
          )}
        </tbody>
        <TbLoadingMask show={serverRender && showLoading} />
      </BsTable>

      <Pagination
        pageSizeOptions={pageSizeOptions}
        count={totalCount}
        pageSize={pageSize}
        page={page}
        {...paginationProps}
        onPageChange={async (_page, _pageSize, _count) => {
          // 不管有沒有onchange 都改變頁數(一般TABLE用)

          if (onPageChange) {
            timerDebounce(async () => {
              if (showLoadingMask) setShowLoading(true);
              await onPageChange(_page + FIRST_PAGE, _pageSize, _count, sort);
              setShowLoading(false);
            }, 600);
          } else {
            setPage(_page);
            setPageSize(_pageSize);
          }
        }}
      />
    </>
  );
};

export default Table;
